/**
 * DataSchemaPanel - Shows available view slugs and column schemas.
 *
 * Props:
 *   selectedViewIds  {string[]}                - IDs of selected views
 *   viewSchemas      {Map<string, viewObject>} - Cached view objects (id → view)
 *   onInsertSchema   {fn(commentString)}       - Called with JSDoc comment to insert
 */

import { useState } from 'react';

function buildJsDocComment(schemas) {
  const lines = ['/**', ' * @param {Object} data - View data keyed by slug'];
  for (const [slug, columns] of Object.entries(schemas)) {
    lines.push(` * @param {Array}  data.${slug}`);
    if (columns && columns.length > 0) {
      for (const col of columns) {
        const typeStr = col.type ? col.type.toLowerCase() : 'any';
        lines.push(` *   @property {${typeStr}} .${col.name}`);
      }
    }
  }
  lines.push(' */');
  return lines.join('\n');
}

export default function DataSchemaPanel({ selectedViewIds = [], viewSchemas = {}, onInsertSchema }) {
  const [collapsed, setCollapsed] = useState(false);

  // Build an ordered list of views that are selected and have cached schema
  const visibleViews = selectedViewIds
    .map((id) => viewSchemas[id])
    .filter(Boolean);

  if (visibleViews.length === 0) return null;

  const handleInsert = () => {
    // Build slug → columns map
    const schemas = {};
    for (const view of visibleViews) {
      const slug = view.slug || view.name || view.id;
      schemas[slug] = view.output_schema?.columns || [];
    }
    const comment = buildJsDocComment(schemas);
    onInsertSchema?.(comment);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-gray-100 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700">Data Schema</span>
          <span className="text-xs bg-gray-300 text-gray-700 rounded px-1.5 py-0.5">
            {visibleViews.length} view{visibleViews.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleInsert(); }}
            className="text-xs px-2 py-0.5 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
            title="Insert JSDoc comment at top of editor"
          >
            Insert Schema
          </button>
          <span className="text-gray-400 text-xs">{collapsed ? '▶' : '▼'}</span>
        </div>
      </div>

      {/* Schema content */}
      {!collapsed && (
        <div className="divide-y divide-gray-200">
          {visibleViews.map((view) => {
            const slug = view.slug || view.name || view.id;
            const columns = view.output_schema?.columns || [];
            return (
              <div key={view.id} className="px-3 py-2">
                <div className="text-xs font-mono font-semibold text-gray-800 mb-1">
                  data.<span className="text-primary-700">{slug}</span>
                  <span className="text-gray-500 font-normal ml-1">— {view.name}</span>
                </div>
                {columns.length === 0 ? (
                  <div className="text-xs text-gray-400 italic">No schema available</div>
                ) : (
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                    {columns.map((col) => (
                      <span key={col.name} className="text-xs font-mono text-gray-600">
                        <span className="text-gray-800">{col.name}</span>
                        {col.type && (
                          <span className="text-gray-400"> : {col.type}</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
